import MapScale from '@/Enums/Map/MapScale.enum';
import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import ReportView from '@/Enums/ReportingDashboard/ReportView/ReportView.enum';
import ViewType from '@/Enums/ReportingDashboard/ViewType.enum';
import type { ITotalReportRecord } from '@/Interfaces/ReportingDashboard/Simplifi/ReportRecord.interface';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Simplifi/State.interface';
import type {
  SimplifiReportOnlyView,
  SimplifiReportView,
  SimplifiZip4Scale,
} from '@/types/ReportingDashboard/Simplifi.type';
import { DEFAULT_LOADING_REQUEST_ID } from '../slice';

export const defaultTotals: ITotalReportRecord = {
  [BeeswaxReportProperties.IMPRESSIONS]: 0,
  [BeeswaxReportProperties.SPEND]: 0,
  // Reach and Frequency start as null so unresolved snapshots render "—"
  // (with a tooltip) rather than a confident "0" before data arrives.
  [BeeswaxReportProperties.REACH]: null,
  [BeeswaxReportProperties.FREQUENCY]: null,
  [BeeswaxReportProperties.VCR]: 0,
  [BeeswaxReportProperties.CTR]: 0,
};

export const initialState: IState = {
  //#region report data
  totals: defaultTotals,

  //#region grid
  dateReport: [],
  channelReport: [],
  deviceReport: [],
  publisherReport: [],
  campaignReport: [],
  creativeReport: [],
  advertiserReport: [],
  dayOfWeekReport: [],
  daypartReport: [],
  placementReport: [],
  dealReport: [],
  geoFenceReport: [],
  geoFenceAttributionReport: [],
  keywordReport: [],
  //#endregion

  // Default to delivery so existing GeoFence behavior is unchanged on first
  // load — the toggle UI flips this to Attribution and back.
  viewType: ViewType.Delivery,

  //#region geo
  stateReport: [],
  dmaReport: [],
  countyReport: [],
  zipReport: [],
  zip4Report: [],
  congressionalDistrictReport: [],
  cityReport: [],
  zipBoundariesData: null,
  stateBoundariesData: null,
  countyBoundariesData: null,
  congressionalDistrictBoundariesData: null,
  dmaBoundariesData: null,
  cityBoundariesData: null,
  //#endregion
  //#endregion

  //#region filters
  campaigns: [],
  //#endregion

  //#region requestIds
  totalsRequestId: DEFAULT_LOADING_REQUEST_ID,

  //#region grid
  dateReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  channelReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  deviceReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  publisherReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  campaignReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  creativeReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  advertiserReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  dayOfWeekReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  daypartReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  placementReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  dealReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  geoFenceReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  geoFenceAttributionReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  keywordReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  //#endregion

  //#region geo
  stateReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  dmaReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  countyReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  zipReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  zip4ReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  congressionalDistrictReportRequestId: DEFAULT_LOADING_REQUEST_ID,
  cityReportRequestId: DEFAULT_LOADING_REQUEST_ID,

  zipBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  stateBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  countyBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  congressionalDistrictBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  dmaBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  cityBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  //#endregion
  //#endregion

  errorCardMessage: '',
  dataIsRefreshing: false,
};

export const simplifiMapScales: MapScale[] = Object.values(MapScale);

// Zip+4 (9-digit ZIP) — Simplifi addressable geofencing. Table-only scale (no
// GeoMap boundary), kept off the global MapScale enum so it doesn't leak into
// other DSPs' geo selectors. See SimplifiZip4Scale.
export const SIMPLIFI_ZIP4_SCALE: SimplifiZip4Scale = 'ZIP+4';

// Geo scales offered in the Impressions-by-Type table's "Geo" dropdown: the
// shared map scales plus the Simplifi-only Zip+4. NOT added to
// `simplifiMapScales` because that list also drives the GeoMap scale selector,
// and Zip+4 has no map boundary.
export const simplifiTableGeoScales: (MapScale | SimplifiZip4Scale)[] = [...simplifiMapScales, SIMPLIFI_ZIP4_SCALE];

export const simplifiReportViews: SimplifiReportOnlyView[] = [
  ReportView.Date,
  ReportView.Channel,
  ReportView.Device,
  ReportView.Advertiser,
  ReportView.Campaign,
  ReportView.Creative,
  ReportView.Publisher,
  ReportView.DaysOfWeek,
  ReportView.Daypart,
  ReportView.Deals,
  ReportView.GeoFence,
  ReportView.Keyword,
];

export const allowedSimplifiTablesToPrint: SimplifiReportView[] = [...simplifiReportViews, ...simplifiTableGeoScales];
